import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import {
  feedDriveService,
  type ConnectorConfig,
  type ConnectorLoadResponse,
  type ConnectorStatus,
} from '@/services/feeddrive';

import styles from './ConnectorLoadPanel.module.css';

interface ConnectorLoadPanelProps {
  connectors: ConnectorStatus[];
  loading: boolean;
  failed: boolean;
}

interface ConsoleValue {
  connectorId: string;
  response?: ConnectorLoadResponse;
  error?: unknown;
}

interface ConnectorGroup {
  category: string;
  connectors: ConnectorStatus[];
}

interface TimingSummary {
  provenance: Record<string, unknown>;
  timing: Record<string, unknown>;
}

interface ConfigEditor {
  connector: ConnectorStatus;
  config?: ConnectorConfig;
  loading: boolean;
  saving: boolean;
  error?: string;
  enabled: boolean;
  frequencySeconds: number;
  limit: number;
  timeoutSeconds: number;
  maxBytes: number;
}

interface LastRun {
  status: string;
  at: string;
  count: number;
}

const CATEGORY_PRIORITY = [
  'Big Tech Releases',
  'IA',
  'China',
  'Texas',
  'Silicon Valley',
  'Geek Filmes e Séries',
  'Geek Jogos',
  'Insiders Social',
  'Insiders Artigos',
  'Mercados',
  'Mercados globais',
  'Macroeconomia mundial',
  'Agro',
  'Agro mundial',
  'Agroclima',
];

function groupConnectors(connectors: ConnectorStatus[]): ConnectorGroup[] {
  const grouped = new Map<string, ConnectorStatus[]>();
  for (const connector of connectors) {
    const category = connector.category || 'Outros';
    grouped.set(category, [...(grouped.get(category) ?? []), connector]);
  }
  return [...grouped.entries()]
    .map(([category, items]) => ({
      category,
      connectors: items.sort((left, right) =>
        left.display_name.localeCompare(right.display_name, 'pt-BR'),
      ),
    }))
    .sort((left, right) => {
      const leftPriority = CATEGORY_PRIORITY.indexOf(left.category);
      const rightPriority = CATEGORY_PRIORITY.indexOf(right.category);
      if (leftPriority >= 0 || rightPriority >= 0) {
        if (leftPriority < 0) return 1;
        if (rightPriority < 0) return -1;
        return leftPriority - rightPriority;
      }
      return left.category.localeCompare(right.category, 'pt-BR');
    });
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function timingSummary(response?: ConnectorLoadResponse): TimingSummary | null {
  const first = objectValue(response?.records[0]);
  const provenance = objectValue(first?.data_provenance);
  const timing = objectValue(first?.data_timing);
  return provenance && timing ? { provenance, timing } : null;
}

function textValue(value: unknown, fallback = '—'): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function timestamp(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('pt-BR');
}

function duration(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (value < 60) return `${Math.round(value)} s`;
  if (value < 3600) return `${Math.round(value / 60)} min`;
  if (value < 86400) return `${(value / 3600).toFixed(1)} h`;
  return `${(value / 86400).toFixed(1)} d`;
}

function errorPayload(error: unknown): unknown {
  if (error instanceof ApiError) return error.body;
  if (error instanceof Error) return { error: { code: 'client_error', message: error.message } };
  return { error: { code: 'unknown_error', message: 'Falha desconhecida' } };
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = objectValue(error.body);
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    if (detail) return `Falha HTTP ${error.status}: ${JSON.stringify(detail).slice(0, 300)}`;
    return `Falha HTTP ${error.status}`;
  }
  if (error instanceof Error) return error.message;
  return 'Falha desconhecida';
}

function numericOption(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function ConnectorLoadPanel({ connectors, loading, failed }: ConnectorLoadPanelProps) {
  const { t } = useTranslation([
    'common',
    'nav',
    'board',
    'articles',
    'analytics',
    'feeddrive',
    'config',
    'strategist',
  ]);
  void t;
  const [running, setRunning] = useState<Set<string>>(() => new Set());
  const [consoleValue, setConsoleValue] = useState<ConsoleValue | null>(null);
  const [configs, setConfigs] = useState<Record<string, ConnectorConfig>>({});
  const [lastRuns, setLastRuns] = useState<Record<string, LastRun>>({});
  const [editor, setEditor] = useState<ConfigEditor | null>(null);

  async function load(connector: ConnectorStatus) {
    setRunning((current) => new Set(current).add(connector.id));
    try {
      const config = configs[connector.id];
      const response = await feedDriveService.loadConnector(connector.id, {
        limit: numericOption(config?.options.manual_limit, 10),
        timeoutSeconds: numericOption(config?.options.manual_timeout_seconds, 30),
        maxBytes: numericOption(config?.options.manual_max_bytes, 8_000_000),
      });
      setConsoleValue({ connectorId: connector.id, response });
      setLastRuns((current) => ({
        ...current,
        [connector.id]: {
          status: response.run.status,
          at: response.run.completed_at,
          count: response.run.record_count,
        },
      }));
    } catch (error) {
      setConsoleValue({ connectorId: connector.id, error });
    } finally {
      setRunning((current) => {
        const next = new Set(current);
        next.delete(connector.id);
        return next;
      });
    }
  }

  async function openConfig(connector: ConnectorStatus) {
    setEditor({
      connector,
      loading: true,
      saving: false,
      enabled: connector.enabled,
      frequencySeconds: 900,
      limit: 10,
      timeoutSeconds: 30,
      maxBytes: 8_000_000,
    });
    try {
      const config =
        configs[connector.id] ?? (await feedDriveService.getConnectorConfig(connector.id));
      setConfigs((current) => ({ ...current, [connector.id]: config }));
      setEditor({
        connector,
        config,
        loading: false,
        saving: false,
        enabled: config.enabled,
        frequencySeconds: config.frequency_seconds,
        limit: numericOption(config.options.manual_limit, 10),
        timeoutSeconds: numericOption(config.options.manual_timeout_seconds, 30),
        maxBytes: numericOption(config.options.manual_max_bytes, 8_000_000),
      });
    } catch (error) {
      setEditor((current) =>
        current ? { ...current, loading: false, error: errorMessage(error) } : null,
      );
    }
  }

  async function saveConfig() {
    if (!editor) return;
    setEditor({ ...editor, saving: true, error: undefined });
    try {
      const options = {
        ...(editor.config?.options ?? {}),
        manual_limit: editor.limit,
        manual_timeout_seconds: editor.timeoutSeconds,
        manual_max_bytes: editor.maxBytes,
      };
      const config = await feedDriveService.updateConnectorConfig(editor.connector.id, {
        enabled: editor.enabled,
        frequency_seconds: editor.frequencySeconds,
        api_tokens: {},
        options,
      });
      setConfigs((current) => ({ ...current, [editor.connector.id]: config }));
      setEditor({ ...editor, config, saving: false });
    } catch (error) {
      setEditor({ ...editor, saving: false, error: errorMessage(error) });
    }
  }

  const renderedJson = consoleValue
    ? JSON.stringify(consoleValue.response ?? errorPayload(consoleValue.error), null, 2)
    : null;
  const connectorGroups = groupConnectors(connectors);
  const timing = timingSummary(consoleValue?.response);

  return (
    <section className={styles.root} aria-labelledby="connector-load-title">
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Operação isolada</span>
          <h2 id="connector-load-title">Cargas manuais</h2>
          <p>Executa um conector uma vez e mostra o JSON pré-ETL. Não grava nem publica.</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={!consoleValue}
          onClick={() => setConsoleValue(null)}
        >
          Limpar tela
        </Button>
      </header>

      {loading && <p className={styles.notice}>Carregando conectores...</p>}
      {failed && (
        <p className={styles.error} role="alert">
          Não foi possível listar os conectores. Tente atualizar a página.
        </p>
      )}
      {!loading && !failed && connectors.length === 0 && (
        <p className={styles.notice}>Nenhum conector registrado.</p>
      )}

      <div className={styles.groups} aria-label="Conectores disponíveis">
        {connectorGroups.map((group, index) => (
          <details
            key={group.category}
            className={styles.group}
            role="region"
            aria-label={group.category}
            open={index < 2}
          >
            <summary className={styles.groupHeader}>
              <h3>{group.category}</h3>
              <span>{group.connectors.length} conectores</span>
            </summary>
            <div className={styles.actions}>
              {group.connectors.map((connector) => {
                const config = configs[connector.id];
                const lastRun = lastRuns[connector.id];
                const enabled = config?.enabled ?? connector.enabled;
                return (
                  <article key={connector.id} className={styles.action}>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      loading={running.has(connector.id)}
                      onClick={() => load(connector)}
                    >
                      Carregar {connector.display_name}
                    </Button>
                    <span>{enabled ? 'agenda ligada' : 'agenda desligada'}</span>
                    <small>{connector.source_class ?? 'fonte não classificada'}</small>
                    <small>{connector.data_delay ?? 'delay não declarado'}</small>
                    {connector.source_url && (
                      <a href={connector.source_url} target="_blank" rel="noreferrer">
                        abrir fonte primária
                      </a>
                    )}
                    <button
                      type="button"
                      className={styles.configButton}
                      onClick={() => openConfig(connector)}
                    >
                      Configurar
                    </button>
                    {lastRun && (
                      <small data-run-status={lastRun.status}>
                        última carga: {lastRun.status} · {lastRun.count} registro(s) ·{' '}
                        {timestamp(lastRun.at)}
                      </small>
                    )}
                  </article>
                );
              })}
            </div>
          </details>
        ))}
      </div>

      {editor && (
        <div className={styles.configBackdrop} role="presentation" onClick={() => setEditor(null)}>
          <section
            className={styles.configPanel}
            role="dialog"
            aria-modal="true"
            aria-label={`Configurar ${editor.connector.display_name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <strong>{editor.connector.display_name}</strong>
                <span>Configuração persistente e isolada</span>
              </div>
              <button type="button" onClick={() => setEditor(null)}>
                fechar
              </button>
            </header>
            {editor.loading ? (
              <p>Carregando configuração real...</p>
            ) : (
              <div className={styles.configFields}>
                <label>
                  <input
                    type="checkbox"
                    checked={editor.enabled}
                    onChange={(event) => setEditor({ ...editor, enabled: event.target.checked })}
                  />
                  Agenda ligada
                </label>
                <label>
                  <span>Intervalo da agenda (segundos)</span>
                  <input
                    type="number"
                    min="10"
                    max="86400"
                    value={editor.frequencySeconds}
                    onChange={(event) =>
                      setEditor({ ...editor, frequencySeconds: Number(event.target.value) })
                    }
                  />
                </label>
                <label>
                  <span>Registros por carga manual</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={editor.limit}
                    onChange={(event) =>
                      setEditor({ ...editor, limit: Number(event.target.value) })
                    }
                  />
                </label>
                <label>
                  <span>Timeout da carga (segundos)</span>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={editor.timeoutSeconds}
                    onChange={(event) =>
                      setEditor({ ...editor, timeoutSeconds: Number(event.target.value) })
                    }
                  />
                </label>
                <label>
                  <span>Limite do JSON (bytes)</span>
                  <input
                    type="number"
                    min="1024"
                    max="10000000"
                    value={editor.maxBytes}
                    onChange={(event) =>
                      setEditor({ ...editor, maxBytes: Number(event.target.value) })
                    }
                  />
                </label>
                {editor.error && <p className={styles.error}>{editor.error}</p>}
                <Button type="button" loading={editor.saving} onClick={saveConfig}>
                  Salvar configuração
                </Button>
              </div>
            )}
          </section>
        </div>
      )}

      <div className={styles.console} aria-live="polite">
        {renderedJson ? (
          <>
            <strong>JSON pré-ETL · {consoleValue?.connectorId}</strong>
            <pre>{renderedJson}</pre>
          </>
        ) : (
          <p>Escolha um conector acima para executar a primeira carga.</p>
        )}
      </div>

      <div className={styles.etlPanels} aria-label="Rastreabilidade temporal">
        <section>
          <header>
            <strong>Pré-ETL</strong>
            <span data-state={timing?.timing.stale ? 'stale' : timing ? 'available' : 'empty'}>
              {timing?.timing.stale ? 'dado defasado' : timing ? 'capturado' : 'aguardando carga'}
            </span>
          </header>
          {timing ? (
            <dl>
              <div>
                <dt>Fonte original</dt>
                <dd>{textValue(timing.provenance.original_source)}</dd>
              </div>
              <div>
                <dt>Fonte de acesso</dt>
                <dd>{textValue(timing.provenance.access_source)}</dd>
              </div>
              <div>
                <dt>Referência</dt>
                <dd>{timestamp(timing.timing.reference_at)}</dd>
              </div>
              <div>
                <dt>Capturado</dt>
                <dd>{timestamp(timing.timing.collected_at)}</dd>
              </div>
              <div>
                <dt>Delay declarado</dt>
                <dd>{textValue(timing.timing.source_delay_label)}</dd>
              </div>
              <div>
                <dt>Idade medida</dt>
                <dd>{duration(timing.timing.data_age_seconds)}</dd>
              </div>
            </dl>
          ) : (
            <p>Execute um conector para medir referência, captura e idade do dado real.</p>
          )}
        </section>
        <section>
          <header>
            <strong>Pós-ETL</strong>
            <span data-state="disconnected">não conectado</span>
          </header>
          <p>
            Nenhum dado normalizado foi produzido. O sistema permanece desconectado nesta etapa;
            tempo de ETL e schema aparecerão somente quando houver uma entrega real.
          </p>
        </section>
      </div>
    </section>
  );
}
